/**
 * Sawyer 06 beds-lockers-runcard (B2, B4) — the two facility-wide lodging
 * choices on Settings → Lodging: the BED SIZES a bed can carry and whether a
 * dog's locker FOLLOWS ITS BED.
 *
 * Sawyer, 2026-10-23 (memo2 [04:01]-[05:01]): sizes "configurable ... rather
 * than like a hard code" (transcribed "considerable"); lockers "default to a
 * number and then ... you can change it ... an option where you can turn
 * off the default globally".
 *
 * Both are operations keys written through `PUT /api/v2/admin/tenant/operations`
 * (`business-config:write:all`: owner, manager), exactly like the capacity
 * threshold beside them; `canEdit` is the card's policy gate (Sawyer 06 seam:
 * `canEditPolicy`, which becomes lane 04's `canEditThreshold`). A role that
 * cannot write sees the values with no control (#484: never a control the
 * server refuses).
 *
 * Deploy window: an older server omits both keys; this block then renders
 * nothing rather than show a policy it cannot save (principle 5).
 *
 * Design Philosophy: #1 one glance (the list reads as text, the switch says
 * On / Off in words), #5 never lie (the source note says "Platform default"
 * until saved), #7 built for hands (44px controls, full-width at 375).
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Input } from '@ferro/ui';
import { adminTenantSettingsApi, type TenantOperationsSettings } from '../../lib/api.js';
import { formatSizeList, parseSizeList } from './lodging-bed-fields.js';
import './LodgingPolicyControls.css';

export interface LodgingPolicyControlsProps {
  apiUrl: string;
  ops: TenantOperationsSettings | null;
  /** The card's policy gate (owner, manager). */
  canEdit: boolean;
  /** The saved settings and the sentence for the card's banner. */
  onSaved: (next: TenantOperationsSettings, message: string) => void;
}

export const LOCKER_FOLLOWS_HELP =
  "When a dog is put on a bed, its locker fills in from that bed's default locker. The desk can change it for the stay.";

export function LodgingPolicyControls({ apiUrl, ops, canEdit, onSaved }: LodgingPolicyControlsProps): JSX.Element | null {
  const sizes = ops?.operations.bedSizeClasses;
  const follows = ops?.operations.lockerFollowsBed;
  const [sizesInput, setSizesInput] = useState(() => formatSizeList(sizes ?? []));
  const [sizesError, setSizesError] = useState<string | null>(null);
  const [sizesBusy, setSizesBusy] = useState(false);
  const [followsBusy, setFollowsBusy] = useState(false);
  const [followsError, setFollowsError] = useState<string | null>(null);

  // A fresh read (another tab, a reload) resets the field to the saved list.
  const savedText = formatSizeList(sizes ?? []);
  useEffect(() => {
    setSizesInput(savedText);
  }, [savedText]);

  if (sizes === undefined && follows === undefined) return null;

  const saveSizes = async () => {
    const parsed = parseSizeList(sizesInput);
    if (!parsed.ok) {
      setSizesError(parsed.message);
      return;
    }
    if (formatSizeList(parsed.sizes) === savedText) {
      setSizesError(null);
      setSizesInput(savedText);
      return;
    }
    setSizesBusy(true);
    setSizesError(null);
    const res = await adminTenantSettingsApi.updateOperations(apiUrl, { bedSizeClasses: parsed.sizes });
    setSizesBusy(false);
    if (res.error || !res.data) {
      // Keep what the owner typed; say what went wrong under it.
      setSizesError(res.error ?? 'Could not save the bed sizes.');
      return;
    }
    const next = res.data.operations.bedSizeClasses ?? [];
    onSaved(
      res.data,
      next.length === 0
        ? 'Bed sizes cleared. Beds are no longer sized.'
        : `Bed sizes are now ${formatSizeList(next)}.`,
    );
  };

  const saveFollows = async (value: boolean) => {
    setFollowsBusy(true);
    setFollowsError(null);
    const res = await adminTenantSettingsApi.updateOperations(apiUrl, { lockerFollowsBed: value });
    setFollowsBusy(false);
    if (res.error || !res.data) {
      setFollowsError(res.error ?? 'Could not save that setting.');
      return;
    }
    onSaved(
      res.data,
      value
        ? "Lockers follow the bed again: a stay with no locker of its own shows its bed's default."
        : "Lockers no longer follow the bed. A locker typed for a stay is kept; printed cards keep theirs.",
    );
  };

  return (
    <dl className="ferro-lodging__facts ferro-lodging-policy" data-testid="lodging-policy">
      {sizes !== undefined ? (
        <div className="ferro-lodging__fact">
          <dt>Bed sizes</dt>
          <dd data-testid="lodging-sizes">
            {canEdit ? (
              <form
                className="ferro-lodging-policy__inline"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void saveSizes();
                }}
                aria-label="Bed sizes"
              >
                <Input
                  value={sizesInput}
                  onChange={(e) => {
                    setSizesInput(e.target.value);
                    setSizesError(null);
                  }}
                  placeholder="e.g. Small, Big"
                  aria-label="Bed sizes, separated by commas"
                  error={sizesError ?? undefined}
                  data-testid="lodging-sizes-input"
                />
                <Button type="submit" size="sm" variant="primary" disabled={sizesBusy} data-testid="lodging-sizes-save">
                  {sizesBusy ? 'Saving…' : 'Save'}
                </Button>
              </form>
            ) : (
              <span className="ferro-lodging-policy__value" data-testid="lodging-sizes-value">
                {sizes.length > 0 ? formatSizeList(sizes) : 'None'}
              </span>
            )}
            <span className="ferro-muted" data-testid="lodging-sizes-note">
              {' '}
              {ops?.source.bedSizeClasses === 'configured' ? 'Separated by commas.' : 'Platform default.'}
            </span>
          </dd>
        </div>
      ) : null}
      {follows !== undefined ? (
        <div className="ferro-lodging__fact">
          <dt>Locker follows bed</dt>
          <dd data-testid="lodging-locker-follows-fact">
            {canEdit ? (
              <div className="ferro-lodging-policy__switch">
                <Checkbox
                  id="lodging-locker-follows"
                  role="switch"
                  aria-checked={follows}
                  checked={follows}
                  disabled={followsBusy}
                  onChange={(e) => void saveFollows(e.target.checked)}
                  label={follows ? 'On' : 'Off'}
                  description={LOCKER_FOLLOWS_HELP}
                  data-testid="lodging-locker-follows"
                />
              </div>
            ) : (
              <>
                <span className="ferro-lodging-policy__value" data-testid="lodging-locker-follows-value">
                  {follows ? 'On' : 'Off'}
                </span>
                <span className="ferro-muted"> {LOCKER_FOLLOWS_HELP}</span>
              </>
            )}
            {followsError ? (
              <div data-testid="lodging-locker-follows-error">
                <Alert variant="error">{followsError}</Alert>
              </div>
            ) : null}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
